import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DocumentService } from 'app/document/services/document.service';
import { OfflineReason, OfflineService } from 'app/services/offline.service';
import { SyncLoggingService } from 'fitforce-logging';
import { get, round } from 'lodash';
import { forkJoin, Observable, of, timer } from 'rxjs';
import { delay, delayWhen, retryWhen, tap } from 'rxjs/operators';

const RETRY_THRESHOLD_MILLIS = 60000;
const RETRY_DELAY_MILLIS = 5000;
const DEFAULT_TITLE = 'FitForce Planner';
const DEFAULT_MESSAGE = `We're checking for updates… don\'t close your browser.`;

@Component({
  selector: 'initialize-load-screen',
  templateUrl: './initialize-load-screen.component.html',
  styleUrls: ['./initialize-load-screen.component.scss'],
})
export class InitializeLoadScreenComponent implements OnInit {
  private modeTimeout = undefined;
  private retryTimeout = undefined;
  private shouldRetry = true;
  public errorState: boolean;

  label = (this.data.label !== undefined) ? this.data.label : true;
  progressBar = (this.data.progressBar !== undefined) ? this.data.progressBar : true;
  downloading = (this.data.downloadingState !== undefined) ? this.data.downloadingState : true;
  progress = 0;
  documentProgress = 0;
  progressModifier = 1;
  mode = 'determinate';
  launchText = this.data.buttonText || 'Launch';
  launchClass = '';
  title: string;
  message: string;

  get totalProgress(): number {
    return (this.progress + this.documentProgress) / this.progressModifier;
  }

  constructor(
    private documentService: DocumentService,
    private dialogRef: MatDialogRef<InitializeLoadScreenComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public syncLog: SyncLoggingService,
    private offline: OfflineService,
  ) {}

  ngOnInit() {
    this.errorState = false;
    this.progress = 0;
    this.documentProgress = 0;
    this.progressModifier = 1;

    if (this.data.sync ) {
      this.syncLog.clear();
    }

    // execute the provided observable if `data.obs` was provided by the caller,
    // otherwise check if `data.sync` was provided and either execute `sync()` or `pull()`
    let obs: Observable<any>;
    if (this.data.obs) {
      obs = this.data.obs;
    } else if (this.data.sync) {
      this.progressModifier = 2;
      obs = forkJoin(
        // this.service.fullSync(),
        this.documentService.sync(),
      );
    } else {
      obs = of(null);
    }

    // use `data.title` and `data.message` if provided, else use default values
    this.title = this.data.title || DEFAULT_TITLE;
    this.message = this.data.message || DEFAULT_MESSAGE;

    // after the dialog has opened, execute the observable
    this.dialogRef.afterOpened().subscribe(() => {
      const startTime = performance.now();

      obs.pipe(
        retryWhen(errors =>
          errors.pipe(
            tap(err => this.retrySync(err)),
            delayWhen(() => timer(RETRY_DELAY_MILLIS)),
          ),
        ),
        tap(() => {
          const endTime = performance.now();
          if (this.data.obs || this.data.sync) {
            this.syncLog.info(`Sync Complete in ${round((endTime - startTime) / 1000, 2)} s.`);
          }
        }),
        // add a delay before completion for a smoother transition when sync is complete
        delay(500),
      )
        .subscribe(
          () => {
            if (this.errorState) {
              this.errorState = false;
            }

            this.downloading = false;
            clearTimeout(this.modeTimeout);

            // automatically close the dialog, if we should
            if (this.data.autoClose) {
              this.dialogRef.close();
              this.progress = 0;
            }
          },
          () => {
            this.errorState = true;
            this.mode = 'determinate';
            this.downloading = false;
          },
        );
    });

    this.documentService.syncProgressSubject.subscribe(progress => {
      this.documentProgress = progress * 100;
      this.progressUpdated();
    });
  }

  progressUpdated() {
    this.mode = 'determinate';

    // switch mode to `indeterminate` if 5 seconds has elapsed since last progress update
    clearTimeout(this.modeTimeout);
    this.modeTimeout = setTimeout(() => {
      if (this.progress < 100) {
        this.mode = 'indeterminate';
      }
    }, 5000);
  }

  /**
   * If sync encountered an error, retry from where the process left off iif within
   * retry threshold, error status is 0 (Unknown) or 503 (Service Unvailable). If the
   * retry threshold has elapsed, the app is forced offline and the error is thrown
   * to abort the sync process and return to the subscriber.
   *
   * If the error is not a result of a timeout or server unavailability, the error is
   * thrown to abort the sync process and return to the subscriber.
   *
   * If the sync was unsuccessful, the user is prompted to either 'Continue Without Syncing'
   * if the server could not be reached, or 'Continue Offline' if the error is not
   * resolvable by retrying.
   *
   * @param err error encountered during sync process
   */
  retrySync(err: any) {
    console.error(err);
    this.progress = 0;
    clearTimeout(this.modeTimeout);
    const message = get(err, 'message', err);
    this.syncLog.error(err, message);

    if (this.shouldRetry && err instanceof HttpErrorResponse && (err.status === 0 || err.status === 503 || err.status === 504)) {
      if (!this.retryTimeout) {
        // set a timeout and set a flag once threshold has elapsed
        this.retryTimeout = setTimeout(() => {
          this.shouldRetry = false;
          clearTimeout(this.retryTimeout);
        }, RETRY_THRESHOLD_MILLIS);
      }

      this.mode = 'indeterminate';
      this.syncLog.info(`Unable to reach the server. Retrying sync in 5 seconds…`);
    } else if (!this.shouldRetry) {
      // retry threshold elapsed, force offline and return error
      this.syncLog.info('Not able to sync the server due to poor connectivity. Switched to offline mode.');
      this.launchText = 'Continue Offline';
      this.launchClass = 'button-launch--warn';
      this.offline.forceOffline(true, OfflineReason.BAD_NETWORK);
      throw err;
    } else {
      // error was not a result of timeout or server unavailability
      this.syncLog.info('An error has occurred during the sync with server.');
      this.launchClass = 'button-launch--error';
      this.launchText = 'Continue Without Syncing';
      throw err;
    }
  }
}
